import React from "react";
import {
  AlertTriangle, ArrowRight, Check, Eye, HelpCircle, KeyRound, MessageCircle, RefreshCw, Send, ShieldCheck,
  Sparkles, User, X
} from "lucide-react";
import { Toast } from "../components/shared/Toast";
import { FormField } from "../components/shared/FormField";
import { Modal } from "../components/shared/Modal";
import { PublicStats } from "../components/shared/PublicStats";
import { useAppContext } from "../AppContext";

const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_ag7-game-recharge/artifacts/jx68vmm8_logo.webp";

/* -----------------------------------------------------------------------------
 * AuthPage — full pre-login experience: login form, register form, 2FA
 * challenge, forgot-password (OTP) wizard. Extracted from App.js.
 * --------------------------------------------------------------------------- */
export function AuthPage() {
  const ctx = useAppContext();
  const {
    toast, publicStats, authMode, setAuthMode, authBusy,
    // Login
    loginEmail, setLoginEmail, loginPassword, setLoginPassword, handleLogin,
    // Register
    regEmail, setRegEmail, regPassword, setRegPassword,
    regCountry, setRegCountry, regPhone, setRegPhone, handleRegister,
    // 2FA challenge
    twoFAChallenge, setTwoFAChallenge, twoFACode, setTwoFACode,
    twoFABusy, handleVerify2FA,
    // Forgot password wizard
    showForgotModal, setShowForgotModal, forgotStep, setForgotStep,
    forgotCountry, setForgotCountry, forgotPhone, setForgotPhone,
    forgotPhoneE164,
    forgotCode, setForgotCode, forgotUsername,
    forgotNewPass, setForgotNewPass, forgotNewPass2, setForgotNewPass2,
    forgotBusy, closeForgot, handleForgotStep1, handleForgotStep2, handleForgotStep3,
  } = ctx;

  return (
      <div data-testid="auth-page" className="min-h-screen bg-[var(--ag7-bg)] bg-[radial-gradient(ellipse_70%_50%_at_50%_-10%,rgba(147,51,234,0.22),transparent_60%),radial-gradient(ellipse_45%_35%_at_10%_100%,rgba(236,72,153,0.10),transparent_60%),radial-gradient(ellipse_50%_35%_at_90%_105%,rgba(6,182,212,0.12),transparent_60%)] text-[var(--ag7-text)] font-sans flex flex-col items-center justify-center p-3 sm:p-4 selection:bg-purple-500 selection:text-white">
        {toast && <Toast toast={toast} />}
        <div className="w-full max-w-md space-y-3 sm:space-y-6 text-center">
          <div className="flex flex-col items-center space-y-1.5 sm:space-y-3">
            <div className="w-14 h-14 sm:w-24 sm:h-24 rounded-xl sm:rounded-2xl overflow-hidden border-2 border-ag7-purple/60 shadow-[0_0_30px_rgba(147,51,234,0.4)] bg-zinc-950 p-1">
              <img src={LOGO_URL} alt="AG7" className="w-full h-full object-cover rounded-lg sm:rounded-xl" />
            </div>
            <h1 className="font-heading text-lg sm:text-2xl font-black tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-500 drop-shadow-[0_2px_16px_rgba(147,51,234,0.35)]">AG7 STORE</h1>
            <p className="text-[10px] sm:text-xs text-zinc-400 tracking-[0.2em] uppercase">RECARGAS PREMIUM</p>
          </div>

          {/* PUBLIC STATS COUNTER */}
          <PublicStats stats={publicStats} />

          <div className="bg-[var(--ag7-glass)] backdrop-blur-xl border border-[var(--ag7-border)] rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-[0_24px_60px_-18px_rgba(0,0,0,0.9),var(--ag7-glow-purple)] text-left space-y-4 sm:space-y-6">
            <div className="flex bg-[var(--ag7-bg)] p-1 rounded-2xl border border-[var(--ag7-border-soft)]">
              <button onClick={() => setAuthMode("login")} data-testid="auth-tab-login"
                className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all duration-150 ${authMode === "login" ? "bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-[0_6px_20px_-6px_rgba(147,51,234,0.6)]" : "text-zinc-400 hover:text-white hover:bg-[var(--ag7-surface-2)]"}`}>
                INICIAR SESIÓN
              </button>
              <button onClick={() => setAuthMode("register")} data-testid="auth-tab-register"
                className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all duration-150 ${authMode === "register" ? "bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-[0_6px_20px_-6px_rgba(147,51,234,0.6)]" : "text-zinc-400 hover:text-white hover:bg-[var(--ag7-surface-2)]"}`}>
                REGISTRARSE
              </button>
            </div>

            {authMode === "login" ? (
              <form data-testid="auth-page-form-1" onSubmit={handleLogin} className="space-y-4">
                <FormField data-testid="login-email-input" label="USUARIO O TELÉFONO" testId="login-email-input" type="text" required value={loginEmail} onChange={setLoginEmail} placeholder="usuario123  ó  +584121234567" />
                <FormField data-testid="login-password-input" label="CONTRASEÑA" testId="login-password-input" type="password" required value={loginPassword} onChange={setLoginPassword} placeholder="••••••••" />
                <div className="text-right -mt-1">
                  <button type="button" onClick={() => { setForgotStep(1); setForgotPhone(""); setForgotCode(""); setForgotNewPass(""); setForgotNewPass2(""); setShowForgotModal(true); }}
                    data-testid="forgot-password-btn"
                    className="text-[11px] font-mono text-cyan-400 hover:text-cyan-300 hover:underline">
                    ¿Olvidaste tu usuario o contraseña?
                  </button>
                </div>
                <button type="submit" disabled={authBusy} data-testid="login-submit-btn"
                  className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-500 hover:opacity-95 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl text-xs shadow-lg shadow-purple-600/30 tracking-wider flex items-center justify-center gap-2">
                  {authBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  <span>{authBusy ? "ENTRANDO..." : "ENTRAR"}</span>
                </button>
              </form>
            ) : (
              <form data-testid="auth-page-form-2" onSubmit={handleRegister} className="space-y-4">
                <FormField data-testid="register-email-input" label="USUARIO" testId="register-email-input" type="text" required value={regEmail} onChange={setRegEmail} placeholder="usuario123" />
                <p className="-mt-2 text-[10px] font-mono text-zinc-500 leading-snug">
                  Alfanumérico, 3-20 caracteres (letras, números, . _ -).
                </p>

                {/* Phone with country code selector */}
                <div>
                  <label className="block text-xs font-mono text-zinc-400 mb-1 uppercase tracking-wider">WhatsApp</label>
                  <div className="flex gap-2">
                    <select
                      value={regCountry}
                      onChange={(e) => setRegCountry(e.target.value)}
                      data-testid="register-country-select"
                      className="w-24 bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-2 py-2.5 text-white text-xs font-mono focus:outline-none focus:border-cyan-500"
                    >
                      <option value="+58">🇻🇪 +58</option>
                      <option value="+57">🇨🇴 +57</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+52">🇲🇽 +52</option>
                      <option value="+51">🇵🇪 +51</option>
                      <option value="+56">🇨🇱 +56</option>
                      <option value="+54">🇦🇷 +54</option>
                      <option value="+593">🇪🇨 +593</option>
                      <option value="+591">🇧🇴 +591</option>
                      <option value="+34">🇪🇸 +34</option>
                      <option value="+55">🇧🇷 +55</option>
                      <option value="+507">🇵🇦 +507</option>
                      <option value="+506">🇨🇷 +506</option>
                      <option value="">+ Otro</option>
                    </select>
                    <input
                      type="tel"
                      required
                      value={regPhone}
                      onChange={(e) => {
                        // Auto-strip a stray leading "0" (Venezuelan users habitually
                        // type "04121234567" but the international format needs it
                        // without the leading zero).
                        let v = e.target.value;
                        if (regCountry === "+58" && /^0\d/.test(v)) {
                          v = v.replace(/^0+/, "");
                        }
                        setRegPhone(v);
                      }}
                      data-testid="register-phone-input"
                      placeholder={regCountry === "" ? "+prefijo y número" : "4121234567"}
                      className="flex-1 bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-2.5 text-white text-xs font-mono focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  {/* Venezuelan warning — the country prefix (+58) already replaces
                      the leading zero. Explicit callout so users don't type "04..." */}
                  {regCountry === "+58" && (
                    <div
                      data-testid="register-phone-warning"
                      className="mt-2 flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-950/20 px-3 py-2 text-[11px] text-amber-200"
                    >
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-400" />
                      <div className="leading-relaxed">
                        <strong className="text-amber-300">Importante:</strong> escribe tu número <strong>sin el 0 inicial</strong>.
                        Ejemplos válidos: <span className="font-mono text-white">412…</span>, <span className="font-mono text-white">424…</span>, <span className="font-mono text-white">422…</span>, <span className="font-mono text-white">414…</span>, <span className="font-mono text-white">416…</span>.
                        <span className="block mt-0.5 text-zinc-400">El código <strong className="text-white">+58</strong> ya reemplaza el 0.</span>
                      </div>
                    </div>
                  )}
                  <p className="text-[10px] font-mono text-zinc-500 mt-1">Recibirás el código de recuperación por WhatsApp a este número.</p>
                </div>

                <FormField data-testid="register-password-input" label="CONTRASEÑA" testId="register-password-input" type="password" required value={regPassword} onChange={setRegPassword} placeholder="mínimo 4 caracteres" autoComplete="new-password" />
                <button type="submit" disabled={authBusy} data-testid="register-submit-btn"
                  className="w-full bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 hover:opacity-95 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl text-xs shadow-lg shadow-cyan-600/30 tracking-wider flex items-center justify-center gap-2">
                  {authBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  <span>{authBusy ? "CREANDO..." : "CREAR CUENTA"}</span>
                </button>
              </form>
            )}
          </div>
        </div>

        {/* ============ FORGOT PASSWORD (3-STEP OTP) ============ */}
        {showForgotModal && (
          <Modal onClose={() => !forgotBusy && closeForgot()} size="sm" testId="forgot-password-modal">
            <div className="flex items-center justify-between border-b border-[var(--ag7-border)] pb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-cyan-950/60 border border-cyan-500 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-base font-heading font-extrabold text-white">Recuperar contraseña</h3>
                  <p className="text-[10px] font-mono text-zinc-500">Paso {forgotStep} de 3 · Por WhatsApp</p>
                </div>
              </div>
              <button data-testid="auth-page-button-3" onClick={() => !forgotBusy && closeForgot()} className="text-zinc-400 hover:text-white bg-ag7-surface-2 border border-[var(--ag7-border-soft)] p-2 rounded-full transition-colors"><X className="w-4 h-4" /></button>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-1 mt-3">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 rounded-full flex-1 transition-colors ${
                    s < forgotStep ? "bg-green-500" : s === forgotStep ? "bg-cyan-500" : "bg-zinc-800"
                  }`}
                  data-testid={`forgot-step-indicator-${s}`}
                />
              ))}
            </div>

            {/* Step 1: phone */}
            {forgotStep === 1 && (
              <form onSubmit={handleForgotStep1} className="space-y-4 pt-4" data-testid="forgot-step-1-form">
                <div>
                  <label className="block text-xs font-mono text-zinc-400 mb-1 uppercase tracking-wider">Número de WhatsApp registrado</label>
                  <div className="flex gap-2">
                    <select
                      value={forgotCountry}
                      onChange={(e) => setForgotCountry(e.target.value)}
                      data-testid="forgot-country-select"
                      className="w-24 bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-2 py-2.5 text-white text-xs font-mono focus:outline-none focus:border-cyan-500"
                    >
                      <option value="+58">🇻🇪 +58</option>
                      <option value="+57">🇨🇴 +57</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+52">🇲🇽 +52</option>
                      <option value="+51">🇵🇪 +51</option>
                      <option value="+56">🇨🇱 +56</option>
                      <option value="+54">🇦🇷 +54</option>
                      <option value="+593">🇪🇨 +593</option>
                      <option value="+591">🇧🇴 +591</option>
                      <option value="+34">🇪🇸 +34</option>
                      <option value="+55">🇧🇷 +55</option>
                      <option value="+507">🇵🇦 +507</option>
                      <option value="+506">🇨🇷 +506</option>
                      <option value="">+ Otro</option>
                    </select>
                    <input
                      type="tel"
                      required
                      value={forgotPhone}
                      onChange={(e) => setForgotPhone(e.target.value)}
                      data-testid="forgot-phone-input"
                      placeholder={forgotCountry === "" ? "+prefijo y número" : "4121234567"}
                      className="flex-1 bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-2.5 text-white text-xs font-mono focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-zinc-400 leading-relaxed">
                  Te enviaremos un código de 6 dígitos por WhatsApp. Válido por 5 minutos.
                </p>
                <button type="submit" disabled={forgotBusy}
                  data-testid="forgot-send-code-btn"
                  className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:opacity-95 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs shadow-lg flex items-center justify-center gap-2">
                  {forgotBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" fill="currentColor" strokeWidth={0} />}
                  <span>{forgotBusy ? "Enviando..." : "Enviar código por WhatsApp"}</span>
                </button>
              </form>
            )}

            {/* Step 2: OTP */}
            {forgotStep === 2 && (
              <form onSubmit={handleForgotStep2} className="space-y-4 pt-4" data-testid="forgot-step-2-form">
                <div className="p-3 rounded-xl bg-cyan-950/30 border border-cyan-500/30 text-[11px] text-cyan-200 font-mono">
                  Enviado al <strong className="text-white">{forgotPhoneE164}</strong>
                </div>
                <div>
                  <label className="block text-xs font-mono text-zinc-400 mb-1 uppercase tracking-wider">Código de 6 dígitos</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    value={forgotCode}
                    onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    data-testid="forgot-code-input"
                    placeholder="000000"
                    className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-2xl font-mono tracking-[0.4em] text-center focus:outline-none focus:border-cyan-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setForgotStep(1); setForgotCode(""); }}
                    disabled={forgotBusy}
                    data-testid="forgot-back-btn"
                    className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 border border-[var(--ag7-border-soft)] text-white font-bold py-3 rounded-xl text-xs disabled:opacity-40 transition-colors"
                  >Volver</button>
                  <button type="submit" disabled={forgotBusy || forgotCode.length !== 6}
                    data-testid="forgot-verify-btn"
                    className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-95 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs">
                    {forgotBusy ? "Verificando..." : "Verificar"}
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: new password */}
            {forgotStep === 3 && (
              <form onSubmit={handleForgotStep3} className="space-y-4 pt-4" data-testid="forgot-step-3-form">
                <div className="p-3 rounded-xl bg-green-950/30 border border-green-500/30 text-[11px] text-green-200 font-mono">
                  ✓ Código verificado{forgotUsername ? <> · Usuario: <strong className="text-white">{forgotUsername}</strong></> : null}
                </div>
                <FormField data-testid="forgot-new-pass-input" label="Nueva contraseña" testId="forgot-new-pass-input" type="password" required value={forgotNewPass} onChange={setForgotNewPass} placeholder="mínimo 4 caracteres" />
                <FormField data-testid="forgot-new-pass-confirm-input" label="Confirmar contraseña" testId="forgot-new-pass-confirm-input" type="password" required value={forgotNewPass2} onChange={setForgotNewPass2} placeholder="repite la nueva contraseña" />
                <button type="submit" disabled={forgotBusy}
                  data-testid="forgot-confirm-btn"
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-500 hover:opacity-95 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs shadow-lg">
                  {forgotBusy ? "Guardando..." : "Guardar nueva contraseña"}
                </button>
              </form>
            )}
          </Modal>
        )}

        {/* ============ 2FA CHALLENGE MODAL ============ */}
        {twoFAChallenge && (
          <Modal onClose={() => !twoFABusy && setTwoFAChallenge(null)} size="sm" testId="twofa-challenge-modal">
            <div className="flex items-center justify-between border-b border-[var(--ag7-border)] pb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-purple-950/60 border border-purple-500 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-base font-heading font-extrabold text-white">Verificación 2FA</h3>
                  <p className="text-[10px] font-mono text-zinc-500 truncate max-w-[220px]">{twoFAChallenge.email}</p>
                </div>
              </div>
              <button data-testid="auth-page-button-4" onClick={() => !twoFABusy && setTwoFAChallenge(null)} className="text-zinc-400 hover:text-white bg-ag7-surface-2 border border-[var(--ag7-border-soft)] p-2 rounded-full transition-colors"><X className="w-4 h-4" /></button>
            </div>
            <form data-testid="auth-page-form-5" onSubmit={handleVerify2FA} className="space-y-4 pt-4">
              <p className="text-xs text-zinc-400">Ingresa los 6 dígitos que muestra tu app autenticadora (Google Authenticator / Authy).</p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="twofa-code-input"
                placeholder="123456"
                autoFocus
                className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-2xl font-mono text-center tracking-[0.5em] focus:outline-none focus:border-purple-500"
              />
              <button type="submit" disabled={twoFABusy || twoFACode.length !== 6}
                data-testid="twofa-verify-btn"
                className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 hover:opacity-95 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs flex items-center justify-center gap-2">
                {twoFABusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                <span>{twoFABusy ? "Verificando..." : "Verificar y entrar"}</span>
              </button>
            </form>
          </Modal>
        )}
      </div>
  );
}
